import { apiService } from "@/lib/api";

const BASE_URL = "/api/v2/undo-requests";

/**
 * Requests to reopen a Hired or Rejected application.
 *
 * Moving a candidate out of a terminal status is a request somebody else
 * approves, and a pending one locks the application's status server-side. Keys
 * are snake_case: these routes set no alias generator.
 */
export const UndoRequestService = {
  /** Raise a request. The server resolves where the candidate would return to. */
  create: async (applicationId, reason) => {
    const response = await apiService.postSecure(BASE_URL, {
      application_id: applicationId,
      reason,
    });
    return response.data;
  },

  get: async (requestId) => {
    const response = await apiService.getSecure(`${BASE_URL}/${requestId}`);
    return response.data;
  },

  /**
   * `scope` is "mine" (raised by the caller), "assigned" (routed to them as a
   * manager) or "all" (every request in the tenant, admins only).
   */
  list: async ({ scope = "mine", status } = {}) => {
    const params = { scope };
    if (status) params.status = status;
    const response = await apiService.getSecure(BASE_URL, { params });
    return response.data;
  },

  /** The note is optional on approval and is what the requester reads. */
  approve: async (requestId, reviewNote) => {
    const body = reviewNote ? { review_note: reviewNote } : {};
    const response = await apiService.postSecure(`${BASE_URL}/${requestId}/approve`, body);
    return response.data;
  },

  /** A rejection says why: the note is required. */
  reject: async (requestId, reviewNote) => {
    const response = await apiService.postSecure(`${BASE_URL}/${requestId}/reject`, {
      review_note: reviewNote,
    });
    return response.data;
  },

  /** Withdraw a request nobody has decided yet. Only its requester may. */
  cancel: async (requestId) => {
    const response = await apiService.postSecure(`${BASE_URL}/${requestId}/cancel`);
    return response.data;
  },
};
